import { useState, useEffect, useRef } from "react";
import {
  Building2, MapPin, Phone, FileText, Save,
  Camera, AlertCircle, CheckCircle, Settings,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { getImageUrl } from "../../utils/getImageUrl";

export default function DeliveryProfile() {
  const [company,  setCompany]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "", nit: "", phone: "", address: "", city: "", description: "",
  });

  useEffect(() => {
    API.get("/delivery-companies/me")
      .then(r => {
        const comp = r.data?.data?.company ?? r.data?.company ?? null;
        setCompany(comp);
        if (comp) {
          setForm({
            name:        comp.name        ?? "",
            nit:         comp.nit         ?? "",
            phone:       comp.phone       ?? "",
            address:     comp.address     ?? "",
            city:        comp.city        ?? "",
            description: comp.description ?? "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
  };

  // ── Crear empresa (primera vez) ──────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("El nombre de la empresa es obligatorio");
    setCreating(true);
    try {
      const res = await API.post("/delivery-companies", form);
      const comp = res.data?.data?.company;
      setCompany(comp);
      toast.success("¡Empresa creada! Ya puedes usar todas las funciones.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al crear la empresa");
    } finally {
      setCreating(false);
    }
  };

  // ── Actualizar perfil ─────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      const logoFile = logoInputRef.current?.files?.[0];
      if (logoFile) fd.append("logo", logoFile);

      const res = await API.put("/delivery-companies/me", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const comp = res.data?.data?.company ?? res.data?.company;
      if (comp) setCompany(comp);
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4 max-w-2xl mx-auto">
        {Array(5).fill(0).map((_, i) => (
          <div key={i} className="h-12 bg-white rounded-xl animate-pulse border border-gray-100" />
        ))}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // Sin empresa: formulario de configuración inicial
  // ────────────────────────────────────────────────────────────
  if (!company) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 w-full max-w-lg p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center shrink-0">
              <Building2 size={24} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900">Configura tu empresa</h2>
              <p className="text-xs text-gray-500">Completa los datos para empezar a operar</p>
            </div>
          </div>

          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 mt-4">
            <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Tu cuenta tiene el rol de empresa de domicilio pero no tienes un perfil registrado.
              Crea tu empresa para acceder a todas las funciones.
            </p>
          </div>

          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Nombre de la empresa <span className="text-red-500">*</span>
              </label>
              <input
                type="text" required
                placeholder="Ej: Domicilios Express"
                value={form.name} onChange={set("name")}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Ciudad</label>
                <input type="text" placeholder="Bogotá"
                  value={form.city} onChange={set("city")}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Teléfono</label>
                <input type="text" placeholder="3001234567"
                  value={form.phone} onChange={set("phone")}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">NIT</label>
              <input type="text" placeholder="900123456-1"
                value={form.nit} onChange={set("nit")}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Dirección</label>
              <input type="text" placeholder="Calle 123 # 45-67"
                value={form.address} onChange={set("address")}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción</label>
              <textarea rows={2} placeholder="Breve descripción de tu empresa..."
                value={form.description} onChange={set("description")}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              />
            </div>

            <button type="submit" disabled={creating}
              className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {creating ? "Creando empresa..." : "Crear empresa de domicilio"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // Con empresa: editar perfil
  // ────────────────────────────────────────────────────────────
  const currentLogo = logoPreview || (company.logo ? getImageUrl(company.logo) : null);

  return (
    <div className="p-4 lg:p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-indigo-100 flex items-center justify-center">
          <Settings size={20} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-black text-gray-900">Mi empresa</h1>
          <p className="text-xs text-gray-500">Edita el perfil de tu empresa de domicilio</p>
        </div>
        {company.verified && (
          <span className="ml-auto flex items-center gap-1 text-xs text-blue-600 bg-blue-50 font-bold px-3 py-1 rounded-full">
            <CheckCircle size={12} /> Verificada
          </span>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div
            className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-indigo-400 transition relative group"
            onClick={() => logoInputRef.current?.click()}
          >
            {currentLogo ? (
              <img src={currentLogo} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <Building2 size={28} className="text-gray-300" />
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center rounded-2xl">
              <Camera size={18} className="text-white" />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700">Logo de la empresa</p>
            <p className="text-xs text-gray-400 mt-0.5">JPG o PNG · máx. 5 MB</p>
            <button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
            >
              Cambiar imagen
            </button>
          </div>
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input type="text" required
              value={form.name} onChange={set("name")}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                <MapPin size={12} className="inline mr-1" />Ciudad
              </label>
              <input type="text" placeholder="Bogotá"
                value={form.city} onChange={set("city")}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                <Phone size={12} className="inline mr-1" />Teléfono
              </label>
              <input type="text"
                value={form.phone} onChange={set("phone")}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <FileText size={12} className="inline mr-1" />NIT
            </label>
            <input type="text" placeholder="900123456-1"
              value={form.nit} onChange={set("nit")}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              <MapPin size={12} className="inline mr-1" />Dirección
            </label>
            <input type="text"
              value={form.address} onChange={set("address")}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción</label>
            <textarea rows={3}
              value={form.description} onChange={set("description")}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
            />
          </div>

          <button type="submit" disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition disabled:opacity-50"
          >
            <Save size={15} />
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </form>
      </div>
    </div>
  );
}
